/**
 * Message Input Component
 * Text input with send button and microphone button for voice recording
 *
 * The input auto-grows up to MAX_LINES then scrolls internally.
 * lineHeight is set explicitly so maxHeight can be derived from it —
 * no runtime measurement needed, works identically on iOS/Android/Web.
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { E2E_MODE } from '../../config/constants';
import { SPEECH_RECOGNITION_AVAILABLE } from '../../config/speechCapabilities';
import Animated, { useAnimatedStyle, interpolate } from 'react-native-reanimated';
import { useReanimatedKeyboardAnimation } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  Box,
  HStack,
} from '@gluestack-ui/themed';
import Ionicons from '../ui/Ionicons';
import { FS_MD } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

const LINE_HEIGHT = Math.ceil(FS_MD * 1.2);
const MAX_LINES = 3;

interface MessageInputProps {
  onSendText: (text: string) => void;
  onStartVoice: () => void;
  /** Blocks everything (input + buttons). Used during sending/streaming. */
  isDisabled?: boolean;
  /** Blocks send + mic but allows typing. Used when socket is disconnected. */
  actionsBlocked?: boolean;
  initialValue?: string;
}

export function MessageInput({
  onSendText,
  onStartVoice,
  isDisabled = false,
  actionsBlocked = false,
  initialValue = '',
}: MessageInputProps) {
  const [text, setText] = useState(initialValue);
  const inputRef = useRef<TextInput>(null);
  const { t } = useTranslation();

  // fontScale: system accessibility value in production, Storybook override in stories.
  // allowFontScaling={false} on TextInput lets us scale manually for both contexts.
  const { theme, fontScale, fontFamily } = useDisplayTheme();
  const scaledLineHeight = Math.ceil(LINE_HEIGHT * fontScale);
  const maxInputHeight = scaledLineHeight * MAX_LINES;

  const handleSend = useCallback(() => {
    if (text.trim().length === 0) return;
    onSendText(text.trim());
    setText('');
  }, [text, onSendText]);

  // On web: Enter sends, Shift+Enter inserts newline
  const handleKeyPress = useCallback((e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (Platform.OS === 'web' && e.nativeEvent.key === 'Enter' && !(e.nativeEvent as unknown as { shiftKey?: boolean }).shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const insets = useSafeAreaInsets();
  const { progress } = useReanimatedKeyboardAnimation();

  // Animate bottom padding: insets.bottom when keyboard is closed → 0 when fully open.
  // progress goes 0 (closed) → 1 (open), so we interpolate inversely.
  const animatedContainerStyle = useAnimatedStyle(() => ({
    paddingBottom: Platform.OS === 'web'
      ? insets.bottom
      : interpolate(progress.value, [0, 1], [insets.bottom, 0]),
  }));

  const buttonsDisabled = isDisabled || actionsBlocked;
  const hasText = text.trim().length > 0;
  // E2E sentinel: a 1×1 transparent View that mounts only when the
  // input is genuinely ready to accept text AND the send/voice
  // actions will fire. Tests wait for this testID before invoking
  // chat.send-message — without it, iOS WDA's typeText silently drops
  // keystrokes when `editable={false}` (isSending / isStreaming), the
  // send button never appears (hasText stays false), and the next
  // `waitFor chat-button-send` times out.
  //
  // Design choices, all to keep the sentinel reliably findable by
  // XCTest on iOS:
  //   - Non-zero size (1×1 absolutely positioned). Zero-sized views
  //     can be pruned from the iOS accessibility tree.
  //   - No `accessible={false}` — that explicitly removes the view
  //     from the a11y tree, which is the SAME tree XCTest's element
  //     search walks. (testID → accessibilityIdentifier; XCUI finds
  //     elements via that identifier on the a11y tree.)
  //   - `pointerEvents="none"` so the invisible sentinel can't steal
  //     a tap from anything underneath it.
  //   - `accessibilityElementsHidden` to keep VoiceOver from
  //     announcing it (this only affects user-facing a11y readers,
  //     not XCTest's element enumeration).
  const inputReady = !isDisabled && !actionsBlocked;

  // Button matches single-line input height (py*2 + lineHeight), with a minimum
  const MIN_BUTTON_SIZE = 40;
  const inputPillHeight = 20 + scaledLineHeight; // py={10} top + bottom + one line
  const buttonSize = Math.max(MIN_BUTTON_SIZE, inputPillHeight);

  return (
    <Animated.View style={[{ backgroundColor: theme.background }, animatedContainerStyle]}>
      {E2E_MODE && inputReady && (
        /*
         * E2E sentinel — gated by the project's `E2E_MODE` flag
         * (`app.config.ts` → `E2E_MODE=true` env var, surfaced via
         * `src/config/constants.ts:E2E_MODE`). Same gate already used
         * by LoginScreen, SignupScreen, ResetPasswordScreen,
         * streamingTTS, and devReset for test-purposed affordances.
         *
         * Choice of `E2E_MODE` over `__DEV__`:
         *   - `__DEV__` is build-mode (debug vs release JS engine);
         *     incidental to whether the build is for E2E.
         *   - `E2E_MODE` is purpose-driven — true exactly when this
         *     build is intended to be driven by E2E tests, regardless
         *     of debug/release. Lets a release-mode E2E binary (no
         *     dev menu, realistic perf) keep the sentinel.
         *
         * Earlier iterations of this sentinel tried to soften the
         * production impact with `accessible={false}` /
         * `accessibilityElementsHidden`, but those props also remove
         * the element from the accessibility tree XCUITest walks
         * (confirmed by the failure capture in run
         * 2026-05-27T10-21-25-075Z: chat-input-message and
         * chat-button-voice were in the elements list, but
         * chat-input-ready was not). Build-flag gating is the correct
         * separation: visible-to-everything in E2E_MODE,
         * invisible-because-not-rendered everywhere else.
         *
         * VISIBILITY: rendered as a small green dot in the top-right
         * of the input row. Visible to humans (so we can confirm at a
         * glance "yes the sentinel is wired up in this build") AND to
         * XCUITest (no a11y-hiding, non-zero size, opaque). Tiny + in
         * the corner so it doesn't disrupt the input UI for the rare
         * cases someone touches an E2E_MODE build (dev iteration,
         * manual debugging, etc.). `pointerEvents="none"` ensures it
         * doesn't intercept taps from controls underneath.
         *
         * Once we've confirmed end-to-end that XCUITest reliably
         * detects this testID across rebuilds, we can revisit and
         * shrink it back to opacity:0. Until then visibility helps
         * us tell "missing because it isn't in the build" from
         * "missing because XCTest can't see it."
         */
        (<View
          testID="chat-input-ready"
          pointerEvents="none"
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            width: 10,
            height: 10,
            borderRadius: 5,
            backgroundColor: "#10b981", // emerald-500
          }}
        />)
      )}
      <HStack
        space="sm"
        alignItems="center"
        px={10}
        py={4}
      >
        {/* Input pill */}
        <Pressable onPress={() => inputRef.current?.focus()} accessible={false} style={{ flex: 1 }}>
          <Box
            style={{
              backgroundColor: theme.card,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.1,
              shadowRadius: 3,
              elevation: 2,
            }}
            rounded="$3xl"
            px={18}
            py={10}
          >
            <TextInput
              ref={inputRef}
              testID="chat-input-message"
              accessibilityLabel={t('chat.input.inputLabel')}
              style={{
                fontSize: FS_MD * fontScale,
                lineHeight: scaledLineHeight,
                padding: 0,
                color: theme.text,
                maxHeight: maxInputHeight,
                ...(fontFamily ? { fontFamily } : undefined),
              }}
              allowFontScaling={false}
              value={text}
              onChangeText={setText}
              placeholder={t('chat.input.placeholder')}
              placeholderTextColor={theme.inputPlaceholder}
              multiline
              numberOfLines={MAX_LINES}
              textAlignVertical="top"
              maxLength={500}
              editable={!isDisabled}
              returnKeyType="default"
              onKeyPress={handleKeyPress}
            />
          </Box>
        </Pressable>

        {/* Single action button: mic when empty, send when text.
            On web the recognizer relay is unavailable (SPEECH_RECOGNITION_AVAILABLE
            is false), so the empty-state mic button is hidden — users type to send. */}
        {(hasText || SPEECH_RECOGNITION_AVAILABLE) && (
        <TouchableOpacity
          testID={hasText ? 'chat-button-send' : 'chat-button-voice'}
          accessibilityLabel={hasText ? t('chat.input.sendButtonLabel') : t('chat.input.voiceButtonLabel')}
          accessibilityRole="button"
          accessibilityState={{ disabled: buttonsDisabled }}
          onPress={hasText ? handleSend : onStartVoice}
          disabled={buttonsDisabled}
          activeOpacity={0.7}
        >
          <Box
            w={buttonSize}
            h={buttonSize}
            style={{
              backgroundColor: theme.primary,
              opacity: buttonsDisabled ? 0.35 : 1,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.15,
              shadowRadius: 4,
              elevation: 3,
            }}
            rounded="$full"
            justifyContent="center"
            alignItems="center"
          >
            <Ionicons
              name={hasText ? 'send' : 'mic'}
              size={22 * fontScale}
              color="#FFFFFF"
            />
          </Box>
        </TouchableOpacity>
        )}
      </HStack>
    </Animated.View>
  );
}
